import { useState } from "react";
import {
  makeEconomy,
  consumptionSegments,
  costPerDistanceSegments,
  fuelTotals,
  fuelLogsForCar,
  buildFuelTrends,
  deriveLedger,
  reminderTypeName,
  type CanonicalStore,
  type Car,
  type FuelLog,
  type TrendSeries,
  type TrendColor,
} from "../../core/index.ts";
import { SplitFlapNumber } from "../components/SplitFlapNumber.tsx";
import { SplineChart } from "../components/SplineChart.tsx";
import { FuelLogForm } from "../forms/FuelLogForm.tsx";
import { TankCalcForm } from "../forms/TankCalcForm.tsx";
import { InsightsView, TrendTable, ProGate } from "./InsightsView.tsx";
import { FillupsView } from "./FillupsView.tsx";
import { dayMonth, fmt, longDate, round0, shortDate } from "../format.ts";

const TREND_COLOR: Record<TrendColor, string> = {
  amber: "var(--amber)", amberHot: "var(--amberHot)", cyan: "var(--cyan)", redline: "var(--redline)", lime: "var(--lime)",
};

type Save = (s: CanonicalStore) => void;

export function FuelView({ store, car, save, isPro, requirePro, onOpenSpend }: {
  store: CanonicalStore; car: Car; save: Save; isPro?: boolean; requirePro?: () => void;
  /** Opens the SPEND tab. The hero's TOTAL SPENT entry links there. */
  onOpenSpend?: () => void;
}) {
  const metric = store.profile.unitSystem === "metric";
  const economy = makeEconomy(metric);
  const currency = store.profile.displayCurrency;
  const logs = fuelLogsForCar(store.fuelLogs, car.id);
  const byDateDesc = [...logs].sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
  const isEV = (car.fuelType ?? "").toLowerCase() === "electric";

  const cons = consumptionSegments(logs, economy);
  const costs = costPerDistanceSegments(logs, economy);
  const totals = fuelTotals(logs, economy);
  const lastElectric = logs.length ? byDateDesc[0]!.kwhAdded != null : isEV;

  const hasTank = !isEV && !!car.tankEstimate && (car.tankEstimate.volume ?? 0) > 0;
  const hasCharge = !!car.chargeEstimate && (car.chargeEstimate.volume ?? 0) > 0;
  const isPHEV = hasTank && hasCharge;

  // "How much is left right now", appended after the trend metrics so the metric
  // the dashboard opens on doesn't move. These carry a `percent` instead of a
  // series: there is no history behind them, so the hero draws a fill bar.
  // The hero spells the level unit out in full — "LITERS", not "L". Trend metrics
  // keep their compound units ("L/100km", "EUR/km"), which don't spell out.
  const fullVolumeUnit = economy.volumeLabel() === "gal" ? "GALLONS" : "LITERS";

  const levels = [
    hasTank ? {
      label: "TANK LEFT",
      value: economy.convertVolume(car.tankEstimate!.volume ?? 0).toFixed(1),
      unit: fullVolumeUnit,
      percent: car.tankEstimate!.percent ?? 0,
      color: "var(--amber)",
      savedAt: car.tankEstimate!.savedAt ?? null,
    } : null,
    hasCharge ? {
      label: "BATTERY LEFT",
      value: (car.chargeEstimate!.volume ?? 0).toFixed(1),
      unit: "KWH",
      percent: car.chargeEstimate!.percent ?? 0,
      color: "var(--cyan)",
      savedAt: car.chargeEstimate!.savedAt ?? null,
    } : null,
  ].filter((x): x is NonNullable<typeof x> => x !== null);

  const [form, setForm] = useState<{ k: "fuel"; log?: FuelLog } | { k: "tank" } | null>(null);
  const [nav, setNav] = useState<{ k: "insights"; initial?: TrendSeries } | { k: "fillups" } | null>(null);

  const series = buildFuelTrends(logs, economy, currency, car);
  const drawable = series.filter((s) => s.points.length >= 2);

  // Every category of spending for this car, not just fuel. deriveLedger merges the
  // manual entries with the fuel/charge and tyre mirrors, which is what SPEND shows.
  const ledger = deriveLedger(store, car.id);

  // The hero cycles every trend the car has data for, then total spend, then the
  // level readouts. Trend entries carry their TrendSeries so tapping the number
  // opens that exact table — the previous hand-written three-item list had to be
  // reverse-matched to a series by title, which broke on PHEV wording.
  const trendMetrics = drawable.map((sr) => ({
    label: sr.title,
    value: fmt(sr.points[sr.points.length - 1]!.value),
    unit: sr.unit,
    series: sr.points.map((p) => p.value),
  }));
  const SPENT_LABEL = "TOTAL SPENT";
  const metrics = [
    ...trendMetrics,
    { label: SPENT_LABEL, value: round0(ledger.total), unit: currency, series: [] as number[] },
    ...levels.map((l) => ({ label: l.label, value: l.value, unit: l.unit, series: [] as number[] })),
  ];
  const [mi, setMi] = useState(0);
  const index = mi % metrics.length;
  const m = metrics[index]!;
  // Non-null only while the hero is parked on a level readout.
  const levelIndex = index - trendMetrics.length - 1;
  const heroLevel = levelIndex >= 0 ? levels[levelIndex]! : null;
  const [heroNav, setHeroNav] = useState<TrendSeries | null>(null);

  // Two dashboard trends: consumption + cost-per-distance (fallback to any drawable).
  const preferred = [
    drawable.find((s) => s.title.includes("CONSUMPTION") || s.title.includes("ENERGY TREND")),
    drawable.find((s) => s.title.startsWith("COST PER") && s.unit.endsWith(economy.distanceLabel())),
  ].filter((x): x is TrendSeries => !!x);
  const dashTrends = [...new Set([...preferred, ...drawable])].slice(0, 2);

  // Two scrolling info lines (parity with the Android FUEL marquees). Top line is
  // usage — how the car is driven and fuelled. Bottom line is state and money —
  // what's left, what it cost, what's fitted, what's due.
  const consLast = cons.length ? fmt(cons[cons.length - 1]!.value) : "—";
  const lastLog = byDateDesc[0];
  const daysSince = lastLog?.date != null
    ? Math.max(0, Math.floor((Date.now() / 1000 - lastLog.date) / 86400))
    : null;
  const currentOdo = logs.reduce((mx, l) => Math.max(mx, l.odometer), 0);

  // Fill-ups per grade, most used first. A charge has no fuel grade, so charges
  // are counted under one ELECTRIC heading rather than invented into one.
  const gradeCounts = (() => {
    const counts = new Map<string, number>();
    for (const l of logs) {
      const key = (l.kwhAdded ?? 0) > 0 && l.volumeAdded <= 0
        ? "ELECTRIC"
        : ((l.fuelGrade ?? "").toUpperCase() || "UNSPECIFIED");
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  })();

  // What's on the car right now.
  //
  // Preferred source is a TyreSet with an open mount interval. But a set only gets
  // a mount log if the swap flow was used — plenty of cars just have individual
  // tyres logged with an install odometer, and those showed nothing at all. Falls
  // back to the most recently installed of those.
  const fittedTyres = (() => {
    for (const set of store.tyreSets.filter((t) => t.carId === car.id)) {
      const open = (set.mountLog ?? []).find((iv) => iv.removedOdometer == null);
      if (!open) continue;
      const runKm = (set.mountLog ?? []).reduce(
        (sum, iv) => sum + Math.max(0, (iv.removedOdometer ?? currentOdo) - iv.mountedOdometer), 0);
      const label = set.name || [set.brand, set.model].filter(Boolean).join(" ") || set.season || "TYRES";
      return { name: label.toUpperCase(), runKm };
    }
    const installed = store.tyres
      .filter((t) => t.carId === car.id && t.installOdometer != null)
      .sort((a, b) => (b.installOdometer ?? 0) - (a.installOdometer ?? 0))[0];
    if (installed?.installOdometer != null) {
      const label = [installed.brand, installed.model].filter(Boolean).join(" ") || installed.size || "TYRES";
      return { name: label.toUpperCase(), runKm: Math.max(0, currentOdo - installed.installOdometer) };
    }
    return null;
  })();

  // Soonest open reminder. Distance-only reminders carry no due date, so those
  // report in distance rather than pretending to know a day.
  const nextDue = (() => {
    const open = store.maintenanceReminders.filter((r) => r.carId === car.id && !r.isCompleted);
    const dated = open.filter((r) => r.dueDate != null).sort((a, b) => (a.dueDate ?? 0) - (b.dueDate ?? 0))[0];
    if (dated?.dueDate != null) {
      const days = Math.round((dated.dueDate - Date.now() / 1000) / 86400);
      const when = days < 0 ? `${Math.abs(days)}D OVERDUE` : days === 0 ? "TODAY" : `IN ${days}D`;
      return `${(dated.title || reminderTypeName(dated.reminderType)).toUpperCase()} ${when}`;
    }
    const byOdo = open.filter((r) => r.dueOdometer != null).sort((a, b) => (a.dueOdometer ?? 0) - (b.dueOdometer ?? 0))[0];
    if (byOdo?.dueOdometer != null && currentOdo > 0) {
      const left = Math.round(economy.convertDistance(byOdo.dueOdometer - currentOdo));
      const when = left < 0
        ? `${Math.abs(left)} ${economy.distanceLabel().toUpperCase()} OVERDUE`
        : `IN ${left} ${economy.distanceLabel().toUpperCase()}`;
      return `${(byOdo.title || reminderTypeName(byOdo.reminderType)).toUpperCase()} ${when}`;
    }
    return null;
  })();

  const traveled = logs.length >= 2
    ? economy.convertDistance(Math.max(...logs.map((l) => l.odometer)) - Math.min(...logs.map((l) => l.odometer)))
    : null;

  const topLine = [
    `${lastElectric ? "AVG ENERGY" : "AVG CONSUMPTION"}: ${consLast} ${economy.economyLabel(lastElectric)}`,
    lastLog?.date ? `LAST ${isEV ? "CHARGE" : "FILL-UP"}: ${longDate(lastLog.date)}` : null,
    daysSince != null ? (daysSince === 0 ? "TODAY" : `${daysSince} DAY${daysSince === 1 ? "" : "S"} AGO`) : null,
    `${totals.count} ${isEV ? "CHARGES" : "FILL-UPS"}`,
    traveled != null ? `TRAVELED: ${round0(traveled)} ${economy.distanceLabel().toUpperCase()}` : null,
    `TOTAL: ${isEV ? `${round0(totals.totalEnergy)} kWh` : `${round0(totals.totalVolumeDisplay)} ${economy.volumeLabel()}`}`,
    ...gradeCounts.map(([grade, n]) => `${grade}: ${n}\u00d7`),
  ].filter(Boolean).join("  ·  ");

  const bottomLine = [
    hasTank ? `TANK LEFT: ${economy.convertVolume(car.tankEstimate!.volume ?? 0).toFixed(1)} ${economy.volumeLabel()}` : null,
    hasCharge ? `BATTERY LEFT: ${(car.chargeEstimate!.volume ?? 0).toFixed(1)} kWh` : null,
    ledger.total > 0 ? `TOTAL SPENT: ${round0(ledger.total)} ${currency}` : null,
    totals.totalCost > 0 ? `ON ${isEV ? "CHARGING" : "FUEL"}: ${round0(totals.totalCost)} ${currency}` : null,
    costs.length ? `COST PER ${economy.distanceLabel().toUpperCase()}: ${fmt(costs[costs.length - 1]!.value)} ${currency}` : null,
    fittedTyres ? `TIRE SET FITTED: ${fittedTyres.name} · ${round0(economy.convertDistance(fittedTyres.runKm))} ${economy.distanceLabel().toUpperCase()}` : null,
    nextDue ? `NEXT: ${nextDue}` : null,
  ].filter(Boolean).join("  ·  ");

  // Tapping the hero opens the thing it is showing: a trend goes to its own table,
  // TOTAL SPENT to the SPEND screen, a level readout to the tank calculator.
  const openHero = () => {
    if (heroLevel) { setForm({ k: "tank" }); return; }
    // Trend entries are the series themselves now, so no title matching.
    if (index < drawable.length) { setHeroNav(drawable[index]!); return; }
    // TOTAL SPENT has no trend table — it belongs to the SPEND screen.
    if (onOpenSpend) onOpenSpend();
    else setNav({ k: "fillups" });
  };

  if (heroNav) {
    return isPro
      ? <TrendTable series={heroNav} onBack={() => setHeroNav(null)} />
      : <ProGate title={heroNav.title} onBack={() => setHeroNav(null)} requirePro={requirePro} />;
  }
  if (nav?.k === "insights") return <InsightsView store={store} car={car} isPro={isPro} requirePro={requirePro} onBack={() => setNav(null)} />;
  if (nav?.k === "fillups") return <FillupsView store={store} car={car} save={save} onBack={() => setNav(null)} />;

  return (
    <div className="container glow">
      <div className="topbar">
        <div className="title">{isEV ? "ENERGY" : "FUEL"} · <span className="car">{car.model}</span></div>
        <button className="btn ghost" style={{ padding: "8px 14px", fontSize: 11 }} onClick={() => setForm({ k: "fuel" })}>+ ADD NEW FILL-UP</button>
      </div>

      <div className="eyebrow amber">{m.label}</div>
      <div className="hero">
        {/* The readout opens whatever it is currently showing: a trend metric goes to
            its own table, a level readout to the tank calculator it came from. */}
        <button
          onClick={openHero}
          style={{
            background: "none", border: 0, padding: 0, margin: 0, cursor: "pointer",
            display: "flex", alignItems: "flex-end", gap: 10, color: "inherit", font: "inherit",
          }}
        >
          <SplitFlapNumber value={m.value} size="6em" color="var(--cream)" />
          <span className="unit">{m.unit}</span>
        </button>
        {/* Inside .hero, not below it: the row's height comes from the 6em number,
            which dwarfs a bar plus a caption, so cycling onto TANK LEFT costs no
            vertical space and the page doesn't jump. */}
        {heroLevel && (
          <span
            onClick={openHero}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openHero(); }}
            style={{
              cursor: "pointer",
              // Stretch to the hero row's height and spread the three parts over it,
              // so the group reads as a companion to the big number, not a footnote.
              flex: "1 1 0%",
              minWidth: 0,
              marginLeft: 10,
              marginRight: 10,
              marginTop: 10,
              paddingBottom: 14,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              // Hairline rule separating the group from the hero number. Parked for
              // now, not deleted — theme-derived from --cream rather than hardcoded,
              // because #f1ebe2 is only the DARK theme's cream and the light themes
              // invert it to near-black, where a fixed value would be invisible.
              // 31% ~= the 0x4f alpha it replaces.
              // borderLeft: "1px solid color-mix(in srgb, var(--cream) 31%, transparent)",
              // paddingLeft: 20,
            }}
          >
            <span style={{ fontFamily: "var(--display)", fontSize: 34, fontWeight: 900, lineHeight: 1, color: heroLevel.color }}>
              {Math.round(heroLevel.percent)}%
            </span>
            <span style={{
              display: "block", height: 6, borderRadius: 3, background: "var(--edge)",
              overflow: "hidden", marginTop: 8, marginBottom: 8,
            }}>
              {/* `key` on the label restarts the sweep when you cycle between
                  TANK LEFT and BATTERY LEFT — without it React reuses the node and
                  the animation only ever plays once. */}
              <span
                key={heroLevel.label}
                className="levelfill"
                style={{
                  display: "block",
                  height: "100%",
                  width: `${Math.min(100, Math.max(0, heroLevel.percent))}%`,
                  borderRadius: 3,
                  background: heroLevel.color,
                }}
              />
            </span>
            <span className="mono muted" style={{
              display: "block", fontSize: 14, lineHeight: 1,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              Last calculation: {shortDate(heroLevel.savedAt)}
            </span>
          </span>
        )}
        {metrics.length > 1 && (
          <span className="arrows">
            <button aria-label="Previous metric" onClick={() => setMi((v) => (v - 1 + metrics.length) % metrics.length)}>▲</button>
            <button aria-label="Next metric" onClick={() => setMi((v) => (v + 1) % metrics.length)}>▼</button>
          </span>
        )}
      </div>

      {byDateDesc.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 4 }}>
          {topLine && <Marquee text={topLine} color="var(--amber)" durationSec={34} />}
          {bottomLine && <Marquee text={bottomLine} color="var(--muted)" durationSec={44} />}
        </div>
      )}

      <div className="statstrip" style={{ marginTop: 16 }}>
        <Tile k={isEV ? "CHARGES" : "FILL-UPS"} v={String(totals.count)} />
        <Tile k={isEV ? "ENERGY" : "VOLUME"} v={isEV ? round0(totals.totalEnergy) : round0(totals.totalVolumeDisplay)} u={isEV ? "kWh" : economy.volumeLabel()} />
        <Tile k="SPENT" v={round0(totals.totalCost)} u={currency} />
      </div>

      {dashTrends.length > 0 && (
        <>
          <div className="divider"><span className="label">{dashTrends.length > 1 ? "TOP TRENDS" : "TOP TREND"}</span><span className="line" /></div>
          <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            {dashTrends.map((s) => (
              <button key={s.title} className="panel" style={{ flex: 1, minWidth: 0, textAlign: "left", cursor: "pointer", padding: 14 }} onClick={() => setNav({ k: "insights" })}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <span className="mono cream" style={{ fontSize: 11, letterSpacing: 1.2 }}>{s.title}</span>
                  <span className="mono muted" style={{ fontSize: 9 }}>{s.unit} ›</span>
                </div>
                <SplineChart values={s.points.map((p) => p.value)} color={TREND_COLOR[s.color]} height={52} />
              </button>
            ))}
          </div>
          <button className="linkrow" style={{ marginTop: 10, justifyContent: "space-between" }} onClick={() => setNav({ k: "insights" })}>
            <span className="mono amber" style={{ fontSize: 10, letterSpacing: 2 }}>INSIGHTS</span>
            <span className="mono amber" style={{ fontSize: 12 }}>›</span>
          </button>
        </>
      )}

      <div className="divider"><span className="label">{isEV ? "CHARGE LOG" : "FILL-UP LOG"}</span><span className="line" /></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {byDateDesc.length === 0 && <div className="panel mono muted" style={{ fontSize: 12 }}>No entries yet. Tap “+ ADD”.</div>}
        {byDateDesc.slice(0, 3).map((l) => (
          <div key={l.id} style={{ cursor: "pointer" }} onClick={() => setForm({ k: "fuel", log: l })}>
            <LogRow log={l} economy={economy} currency={currency} allLogs={logs} />
          </div>
        ))}
      </div>
      {byDateDesc.length > 3 && (
        <button className="linkrow" style={{ marginTop: 10, justifyContent: "space-between" }} onClick={() => setNav({ k: "fillups" })}>
          <span className="mono amber" style={{ fontSize: 10, letterSpacing: 2 }}>VIEW ALL {byDateDesc.length} {isEV ? "CHARGES" : "FILL-UPS"}</span>
          <span className="mono amber" style={{ fontSize: 12 }}>›</span>
        </button>
      )}

      <button className="linkrow" style={{ marginTop: 16, justifyContent: "space-between", paddingLeft: 0 }} onClick={() => setForm({ k: "tank" })}>
        {isPHEV ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left", paddingLeft: 10 }}>
              <div className="mono" style={{ fontSize: 10, letterSpacing: 2 }}><span className="amber">TANK LEFT</span><span className="muted"> / </span><span className="cyan">BATTERY LEFT</span></div>
              <span className="mono muted" style={{ fontSize: 9 }}>Last calculation on: {longDate(car.tankEstimate?.savedAt ?? null)}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="mono" style={{ fontSize: 18, fontWeight: 700, color: "var(--amber)" }}>{economy.convertVolume(car.tankEstimate!.volume ?? 0).toFixed(1)}</span>
              <span className="mono muted" style={{ fontSize: 9 }}>{economy.volumeLabel()}</span>
              <span className="mono muted" style={{ fontSize: 11 }}>/</span>
              <span className="mono" style={{ fontSize: 18, fontWeight: 700, color: "var(--cyan)" }}>{(car.chargeEstimate!.volume ?? 0).toFixed(1)}</span>
              <span className="mono muted" style={{ fontSize: 9 }}>kWh</span>
              <span className="mono amber" style={{ fontSize: 14 }}>›</span>
            </div>
          </>
        ) : hasTank || hasCharge ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left", paddingLeft: 10 }}>
              <span className={`mono ${hasCharge ? "cyan" : "amber"}`} style={{ fontSize: 10, letterSpacing: 2 }}>{hasCharge ? "BATTERY" : "TANK"} LEFT</span>
              <span className="mono muted" style={{ fontSize: 9 }}>Last calculation on: {longDate((hasCharge ? car.chargeEstimate : car.tankEstimate)?.savedAt ?? null)}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="mono" style={{ fontSize: 20, fontWeight: 700, color: hasCharge ? "var(--cyan)" : "var(--cream)" }}>
                {hasCharge ? (car.chargeEstimate!.volume ?? 0).toFixed(1) : economy.convertVolume(car.tankEstimate?.volume ?? 0).toFixed(1)}
              </span>
              <span className="mono muted" style={{ fontSize: 9 }}>{hasCharge ? "kWh" : economy.volumeLabel()}</span>
              <span className="mono amber" style={{ fontSize: 14 }}>›</span>
            </div>
          </>
        ) : (
          <div className="amber">OPEN {isEV ? "BATTERY" : "TANK"} CALCULATOR</div>
        )}
      </button>

      {form?.k === "fuel" && <FuelLogForm store={store} car={car} editing={form.log} onSave={save} onClose={() => setForm(null)} />}
      {form?.k === "tank" && <TankCalcForm store={store} car={car} onSave={save} onClose={() => setForm(null)} />}
    </div>
  );
}

/** How many times the text is repeated inside the marquee track. See MARQUEE_COPIES
 *  in theme.css — the keyframe shifts by exactly one copy (100 / 5 = 20%). */
const MARQUEE_COPIES = 5;

function Marquee({ text, color, durationSec = 34 }: { text: string; color?: string; durationSec?: number }) {
  // Two copies is enough for the -1-copy keyframe to be mathematically seamless,
  // but only if the pair is wider than the viewport. Short lines aren't: the first
  // copy scrolls off before the second arrives and you see the gap. Repeating five
  // times guarantees the track overflows on any realistic width. The shift is still
  // exactly one copy, so the scroll speed is unchanged.
  return (
    <div className="marquee">
      <div className="marquee__inner" style={{ animationDuration: `${durationSec}s` }}>
        {Array.from({ length: MARQUEE_COPIES }, (_, i) => (
          <span key={i} className="mono" style={{ color }} aria-hidden={i > 0 ? "true" : undefined}>
            {text}
          </span>
        ))}
      </div>
    </div>
  );
}

function Tile({ k, v, u }: { k: string; v: string; u?: string }) {
  return (
    <div className="stattile">
      <div className="k">{k}</div>
      <SplitFlapNumber value={v} size={20} weight={500} />
      {u ? <div className="u">{u}</div> : <div style={{ height: 12 }} />}
    </div>
  );
}

/** One "Label: value" line, matching the mobile fill-up rows. */
function LabelledLine({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ display: "flex", gap: 5, minWidth: 0 }}>
      <span className="muted" style={{ flex: "0 0 auto" }}>{label}</span>
      <span style={{ color, letterSpacing: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}

/**
 * One fill-up / charge. Every line is spelled out — "Grade:", "Station:" and so on —
 * rather than led by an icon, matching the mobile rows.
 *
 * `allLogs` is optional only so existing call sites keep working; without it the
 * distance-derived lines (distance, consumption, price per 100) can't be computed,
 * since they need the previous entry's odometer.
 */
export function LogRow({ log, economy, currency, allLogs }: {
  log: FuelLog;
  economy: ReturnType<typeof makeEconomy>;
  currency: string;
  allLogs?: FuelLog[];
}) {
  const { d, m } = dayMonth(log.date ?? null);
  const kwh = log.kwhAdded ?? 0;
  const combined = log.volumeAdded > 0 && kwh > 0;
  const displayVol = economy.convertVolume(log.volumeAdded);
  const displayOdo = economy.convertDistance(log.odometer);
  const dLabel = economy.distanceLabel().toUpperCase();
  const vLabel = economy.volumeLabel().toUpperCase();

  // Segment distance: this entry's odometer minus the previous entry's, in km.
  const segKm = (() => {
    if (!allLogs) return null;
    const ordered = [...allLogs].sort((a, b) => a.odometer - b.odometer);
    const i = ordered.findIndex((l) => l.id === log.id);
    if (i <= 0) return null;
    const dist = log.odometer - ordered[i - 1]!.odometer;
    return dist > 0 ? dist : null;
  })();

  const fuelEff = segKm != null ? economy.consumptionValue(segKm, log.volumeAdded, false) : null;
  const elecEff = segKm != null && kwh > 0 ? economy.consumptionValue(segKm, kwh, true) : null;
  const segDisplay = segKm != null ? economy.convertDistance(segKm) : null;

  return (
    <div className="logrow">
      <div className="date"><div className="d">{d}</div><div className="m">{m}</div></div>
      <div className="sep" />
      <div className="mid">
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span className="cream">{round0(displayOdo)}</span>
          <span className="muted">{dLabel}</span>
          {log.isFullFill && <span className="badge">FULL</span>}
          <span style={{ flex: 1 }} />
          <span className="cream" style={{ fontSize: 17 }}>{log.totalCost.toFixed(2)}</span>
          <span className="muted" style={{ fontSize: 9 }}>{currency}</span>
        </div>

        {combined ? (
          <>
            <LabelledLine label="Grade:" value={(log.fuelGrade ?? "").toUpperCase()} color="var(--creamDim)" />
            <LabelledLine label="Grade:" value="ELECTRIC" color="var(--cyan)" />
          </>
        ) : (
          <LabelledLine
            label="Grade:"
            value={kwh > 0 ? "ELECTRIC" : (log.fuelGrade ?? "").toUpperCase()}
            color={kwh > 0 ? "var(--cyan)" : "var(--creamDim)"}
          />
        )}

        {log.stationName && <LabelledLine label="Station:" value={log.stationName} color="var(--muted)" />}

        {segDisplay != null && (
          <LabelledLine label="Distance:" value={`${round0(segDisplay)} ${dLabel}`} color="var(--creamDim)" />
        )}
        {fuelEff != null && (
          <LabelledLine label="Avg. Consume:" value={`${fuelEff.toFixed(2)} ${economy.efficiencyLabel().toUpperCase()}`} color="var(--amber)" />
        )}
        {elecEff != null && (
          <LabelledLine label="Avg. Consume:" value={`${elecEff.toFixed(2)} ${economy.energyEfficiencyLabel().toUpperCase()}`} color="var(--cyan)" />
        )}
        {/* Per-100 is what this figure has always been; the label says so rather
            than claiming a per-1 price the number isn't. */}
        {segDisplay != null && segDisplay > 0 && (
          <LabelledLine
            label={`Price per 100 ${dLabel}:`}
            value={`${((log.totalCost / segDisplay) * 100).toFixed(2)} ${currency}`}
            color="var(--amber)"
          />
        )}

        {log.volumeAdded > 0 && (
          <LabelledLine
            label={`Vol. & Price per ${vLabel}:`}
            value={log.volumeAdded > 0 && log.totalCost > 0 && kwh === 0
              ? `${displayVol.toFixed(1)} ${vLabel} @ ${(log.totalCost / displayVol).toFixed(2)} ${currency}`
              : `${displayVol.toFixed(1)} ${vLabel}`}
            color="var(--amber)"
          />
        )}
        {kwh > 0 && (
          <LabelledLine
            label="Vol. & Price per KWH:"
            value={!combined && log.totalCost > 0
              ? `${kwh.toFixed(1)} KWH @ ${(log.totalCost / kwh).toFixed(2)} ${currency}`
              : `${kwh.toFixed(1)} KWH`}
            color="var(--cyan)"
          />
        )}

        {log.notes && <LabelledLine label="Note:" value={log.notes} color="var(--muted)" />}
      </div>
    </div>
  );
}
